import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { type AdviceInput, AdviceInputSchema } from "@/features/advice/domain/advice.schema";
import { InlineError } from "@/shared/components/data/ErrorState";
import { FormSheet } from "@/shared/components/data/FormSheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { getApiErrorField, getApiErrorMessage } from "@/shared/lib/http-errors";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  useCreateAdviceMutation,
  useListAdviceCategoriesQuery,
  useUpdateAdviceMutation,
} from "@/store/apis/adminApi.enhanced";
import type { Advice } from "@/store/apis/adminApi.enhanced";

interface AdviceFormSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Si viene, es modo edición; si no, modo create. */
  advice?: Advice | null;
}

const BODY_MAX = 400;
const FORM_ID = "advice-form";

/**
 * Form de create/edit de consejo. Se abre en un FormSheet (shared) para
 * tener el dirty-guard y el footer sticky estandarizado.
 *
 * Decisiones:
 *   - El select de categoría carga de `useListAdviceCategoriesQuery`
 *     con un `limit` alto (100) — las categorías son pocas por diseño,
 *     no paginamos acá.
 *   - Si el fetch de categorías falla, mostramos un error inline y
 *     deshabilitamos el submit (sin categoría no se puede crear).
 *   - El body tiene contador en vivo 0/400 con color amber cuando pasa
 *     el 90% para avisar antes del límite.
 */
export function AdviceFormSheet({ open, onOpenChange, advice }: AdviceFormSheetProps) {
  const isEdit = Boolean(advice);

  const categoriesQuery = useListAdviceCategoriesQuery(
    { limit: 100, sort: "position" },
    { skip: !open },
  );

  const [createAdvice, createState] = useCreateAdviceMutation();
  const [updateAdvice, updateState] = useUpdateAdviceMutation();
  const submitting = createState.isLoading || updateState.isLoading;
  const submitError = createState.error ?? updateState.error;

  const form = useForm<AdviceInput>({
    resolver: zodResolver(AdviceInputSchema),
    defaultValues: useMemo(
      () => ({
        category: advice?.category ?? "",
        body: advice?.body ?? "",
        position: advice?.position,
        active: advice?.active ?? true,
      }),
      [advice],
    ),
    mode: "onBlur",
  });

  // Resetear form cada vez que cambia el advice (edit → otro edit) o se abre.
  useEffect(() => {
    if (open) {
      form.reset({
        category: advice?.category ?? "",
        body: advice?.body ?? "",
        position: advice?.position,
        active: advice?.active ?? true,
      });
    }
    // Intencional: resetear al abrir / cambiar advice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, advice?.id]);

  const bodyLen = form.watch("body")?.length ?? 0;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (isEdit && advice) {
        await updateAdvice({ id: advice.id, body: values }).unwrap();
        toast.success("Consejo actualizado");
      } else {
        await createAdvice({ body: values }).unwrap();
        toast.success("Consejo creado");
      }
      onOpenChange(false);
    } catch (err) {
      // Si el back nos dice qué campo causó el conflicto (ej. `position`
      // en consejos duplicados), anclamos el error al input específico
      // en vez de mostrar solo el InlineError genérico. RHF limpia el
      // error en el próximo blur válido del campo (mode: "onBlur").
      const field = getApiErrorField(err);
      if (field && field in form.getValues()) {
        form.setError(field as keyof AdviceInput, {
          type: "server",
          message: getApiErrorMessage(err),
        });
      }
      // El InlineError general sigue cubriendo errores sin `field`.
    }
  });

  const categoriesError = categoriesQuery.isError;
  const categoriesLoading = categoriesQuery.isLoading;
  const hasCategories = (categoriesQuery.data?.items.length ?? 0) > 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "Editar consejo" : "Nuevo consejo"}
      description={
        isEdit
          ? "Los cambios se publican al guardar."
          : "Define el contenido que aparecerá en el card 'Consejo del día'."
      }
      formId={FORM_ID}
      submitLabel={isEdit ? "Guardar cambios" : "Crear consejo"}
      submitting={submitting}
      submitDisabled={categoriesError || !hasCategories}
      dirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={onSubmit} className="space-y-5" noValidate>
          {/* Categoría */}
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Categoría
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={categoriesLoading || categoriesError}
                >
                  <FormControl>
                    <SelectTrigger className="h-11 text-[13.5px]">
                      <SelectValue
                        placeholder={
                          categoriesLoading ? "Cargando categorías…" : "Elige una categoría"
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categoriesQuery.data?.items.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>
                        <span className="inline-flex items-center gap-2">
                          {cat.emoji ? <span aria-hidden>{cat.emoji}</span> : null}
                          <span>{cat.label}</span>
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {cat.slug}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {categoriesError ? (
            <InlineError
              error={categoriesQuery.error}
              fallback="No se pudieron cargar las categorías."
            >
              Crea al menos una categoría en {"“"}Consejos → Categorías{"”"} antes de agregar
              consejos.
            </InlineError>
          ) : null}

          {/* Body */}
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-baseline justify-between">
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Consejo
                  </FormLabel>
                  <span
                    className={cn(
                      "font-mono text-[11px] tabular-nums",
                      bodyLen > BODY_MAX
                        ? "text-destructive"
                        : bodyLen > BODY_MAX * 0.9
                          ? "text-amber-700"
                          : "text-muted-foreground",
                    )}
                  >
                    {bodyLen}/{BODY_MAX}
                  </span>
                </div>
                <FormControl>
                  <Textarea
                    rows={6}
                    maxLength={BODY_MAX + 20}
                    placeholder="Escribe un consejo breve, directo y empático…"
                    className="resize-none text-[14px] leading-relaxed transition-shadow focus-visible:shadow-ring-focus"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Position + Active en una fila */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Posición
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      placeholder="Auto"
                      className="h-11 text-[13.5px]"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Estado
                  </FormLabel>
                  <div
                    className={cn(
                      "flex h-11 items-center justify-between rounded-md border bg-background px-3",
                      "text-[13.5px]",
                    )}
                  >
                    <span className={field.value ? "text-foreground" : "text-muted-foreground"}>
                      {field.value ? "Activo" : "Pausado"}
                    </span>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Error de submit */}
          <InlineError
            error={submitError}
            fallback={isEdit ? "No pudimos guardar los cambios." : "No pudimos crear el consejo."}
          />
        </form>
      </Form>
    </FormSheet>
  );
}
